import React, { useState, useEffect, useRef } from 'react'
import s from '@/features/modules/moduleDetail.module.css'
import Prism from 'prismjs'
import 'prismjs/components/prism-hcl'
import { DepGraph } from '@/features/graph/DepGraph'
import { useVersionDetail, useVersionGraph, useOrgPolicySettings, isArtefactPolicyEnabled } from '@/lib/hooks'
import { api } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import { PageLoading } from '@/shared/components/EmptyState'
import { useToast } from '@/context/ToastContext'
import { categoryFindingBadge } from '@/features/modules/components/categoryFindingBadge'
import { CategoryFindingsTab } from '@/features/modules/tabs/CategoryFindingsTab'
import { OverviewTab } from '@/features/modules/tabs/OverviewTab'
import { type Tab } from '@/features/modules/tabs/moduleTabs'
import { Tabs } from '@/shared/components/Tabs'

export function VersionDetailTabs({ slug, name, provider, version, canOwner }: { slug: string; name: string; provider: string; version: string; canOwner: boolean }) {
  const detail  = useVersionDetail(slug, name, provider, version)
  const graph  = useVersionGraph(slug, name, provider, version)
  const policySettings = useOrgPolicySettings(slug)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [rescanBusy, setRescanBusy]   = useState(false)
  const [overrideBusy, setOverrideBusy] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [showOverrideForm, setShowOverrideForm] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    const status = detail.data?.status
    if (status === 'pending' || status === 'processing') {
      pollRef.current = setInterval(() => detail.refetch(), 5000)
    } else {
      if (pollRef.current) clearInterval(pollRef.current)
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [detail.data?.status, detail.refetch])

  async function handleRescan() {
    setRescanBusy(true)
    try {
      await api.post(`/v1/modules/${slug}/${name}/_/${version}/rescan`, {})
      showToast('Rescan queued.', 'success')
      detail.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setRescanBusy(false)
    }
  }

  async function handleOverride() {
    if (!overrideReason.trim()) return
    setOverrideBusy(true)
    try {
      await api.post(`/v1/modules/${slug}/${name}/_/${version}/override-consumable`, { reason: overrideReason.trim() })
      showToast('Override applied. Version is now consumable.', 'success')
      setShowOverrideForm(false)
      setOverrideReason('')
      detail.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setOverrideBusy(false)
    }
  }

  if (detail.loading) return <PageLoading message="Loading version details…" />
  if (!detail.data) return null

  const { status, findings, usage_example, readme, consumable, consumable_override, manually_blocked, block_reason, blocked_by_name, blocked_at } = detail.data
  const scanning = status === 'pending' || status === 'processing'

  const modulesPolicyEnabled = isArtefactPolicyEnabled(policySettings.data, 'modules')
  const isBlocked = !consumable && !consumable_override

  const tabs: { key: Tab; label: string; badge?: React.ReactNode }[] = [
    { key: 'overview',          label: 'Overview' },
    { key: 'usage',             label: 'Usage' },
    { key: 'misconfigurations', label: 'Findings', badge: scanning ? <span className="spinner" style={{ width: 9, height: 9, marginLeft: 6 }} /> : categoryFindingBadge(findings, 'misconfiguration') },
    { key: 'secrets',           label: 'Secrets',           badge: scanning ? <span className="spinner" style={{ width: 9, height: 9, marginLeft: 6 }} /> : categoryFindingBadge(findings, 'secret') },
    { key: 'graph',             label: 'Dep Graph' },
  ]

  return (
    <div className="fu" style={{ marginBottom: 24 }}>
      {/* Manual block banner — distinct from policy block */}
      {manually_blocked && (
        <div className={s.blockedBanner}>
          <span className={s.blockedText}>
            ✗ This version has been manually blocked
            {blocked_by_name ? ` by ${blocked_by_name}` : ''}
            {blocked_at ? ` on ${formatDateTime(blocked_at)}` : ''}
            {block_reason ? ` - ${block_reason}` : '.'}
          </span>
        </div>
      )}
      {/* Blocked banner — only when modules policies are actually enabled. If they're
          off, a lingering consumable=0 is a transient the backfill/recompute resolves;
          we don't claim "blocked by policy" when policy enforcement is disabled. */}
      {isBlocked && modulesPolicyEnabled && (
        <div className={s.blockedBanner}>
          <span className={s.blockedText}>
            ✗ This version is blocked by policy and cannot be downloaded by Terraform.
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm" disabled={rescanBusy} onClick={handleRescan}>
            {rescanBusy ? <span className="spinner" /> : 'Rescan'}
          </button>
          {canOwner && !showOverrideForm && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--amber)' }} onClick={() => setShowOverrideForm(true)}>
              Override
            </button>
          )}
        </div>
      )}
      {consumable_override && (
        <div className={s.overrideBanner}>
          ⚠ Consumable override is active - policy failures are being ignored for this version.
        </div>
      )}
      {showOverrideForm && (
        <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', marginBottom: 16, display: 'grid', gap: 10 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--amber)' }}>Override reason (required)</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              style={{ flex: 1 }}
              placeholder="e.g. Approved by security team - ticket SEC-123"
              maxLength={1000}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
            <button className="btn btn-primary btn-sm" style={{ background: 'var(--amber)', borderColor: 'var(--amber)' }} disabled={overrideBusy || !overrideReason.trim()} onClick={handleOverride}>
              {overrideBusy ? <span className="spinner" /> : 'Confirm override'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowOverrideForm(false)}>Cancel</button>
          </div>
        </div>
      )}
      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={setActiveTab}
        label="Version sections"
        right={
          <div style={{ padding: '0 12px', flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" disabled={detail.refreshing} onClick={() => detail.refetch()}>
              {detail.refreshing ? <span className="spinner" /> : '↻'}
            </button>
          </div>
        }
      />

      {/* Tab content - flush-left to align with the tab bar and version table above */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab readme={readme} scanning={scanning} />
        )}

        {activeTab === 'usage' && (
          <div className="code-block">
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text3)', marginBottom: 8 }}>
              // .tf usage - v{version}
              {scanning && (
                <span style={{ marginLeft: 12, color: 'var(--text3)' }}>
                  <span className="spinner" style={{ width: 10, height: 10, marginRight: 4 }} />scan pending…
                </span>
              )}
            </div>
            {usage_example ? (
              <pre style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                <code
                  className="language-hcl"
                  dangerouslySetInnerHTML={{
                    __html: Prism.highlight(usage_example, Prism.languages.hcl, 'hcl'),
                  }}
                />
              </pre>
            ) : (
              <div style={{ color: 'var(--text3)', fontSize: '0.82rem' }}>
                {scanning ? 'Usage example will appear after scan completes.' : 'No usage example available.'}
              </div>
            )}
          </div>
        )}

        {(activeTab === 'misconfigurations' || activeTab === 'secrets') && (
          <CategoryFindingsTab
            category={activeTab === 'secrets' ? 'secret' : 'misconfiguration'}
            findings={findings}
            scanning={scanning}
          />
        )}

      </div>

      {/* Graph tab - rendered outside padded wrapper with explicit bounded container so ReactFlow fitView resolves correctly */}
      {activeTab === 'graph' && (
        <div style={{ padding: '0 0 20px' }}>
          <div style={{ height: 480, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <DepGraph
              data={graph.data || { nodes: [], edges: [] }}
              loading={graph.loading}
              error={graph.error}
              height="100%"
              /* Without this the node bodies treated every node as unowned while the context
                 menu treated it as owned, so hover hid the governance actions the menu then
                 offered. */
              currentOrg={slug}
              focusNodeId={`mod:${slug}/${name}/${provider}`}
            />
          </div>
        </div>
      )}
    </div>
  )
}
